// Finance fixtures: Accounting (summary + payouts), Analytics, Disputes, and the
// Client Admin Stripe panels. A single 30-day daily series with periodic peak-day
// spikes is the backbone so Accounting and Analytics agree. Every scale knob —
// daily baseline, peak-day bonus, session counts, splits, day/hour curves — comes
// from the active client's `finance` config, so the same math renders a small
// event venue or a ~1M-sessions/yr park.

import {
  DEMO_ZONES, DEMO_CLIENT, ZONE_MAIN, ZONE_NORTH, ZONE_VIP, zoneName,
  DEMO_PLATES, DEMO_EMAILS,
} from '../constants'
import {
  dateOnlyDaysFromNow, unixDaysFromNow, isoDaysFromNow,
} from '../time'
import { OPEN_PERIOD_EXPORT_MESSAGE, rangeIncludesOpenMonth } from '@parkify/api-types'
// Annotating the fixtures with the same types the Accounting screen reads is the
// point of the exercise: a field the Worker renames can no longer be right in the
// console and quietly stale here.
import type {
  AccountingSourceBlocker,
  AccountingSummaryResponse, Payout, PayoutDetailResponse, PayoutListResponse,
  DisputeResponse, DisputesListResponse, DisputeStatus, DisputeTracking,
  PlatformTakeRateResponse,
  ReconciliationResponse,
  TransferReconResponse, ConnectPayoutTraceResponse,
  PayoutStatement, PayoutStatementLine,
} from '@parkify/api-types'

const F = DEMO_CLIENT.finance
const PLATFORM_FEE_BPS = F.platformFeeBps

interface Day {
  date: string
  revenueCents: number
  sessions: number
  refundsCents: number
  chargebacksCents: number
  refundCount: number
  netCents: number
}

// 30-day daily series ending today. Peak days land every ~N days; today is a
// partial (in-progress) day so the latest bar isn't a full peak. All money in CENTS.
function buildDaily(): Day[] {
  const days: Day[] = []
  for (let d = 29; d >= 0; d--) {
    const dow = new Date(Date.now() - d * 86_400_000).getDay()
    const isPeak = d % F.peakEveryNDays === 2 && d !== 0 // periodic peaks, never "today"
    const weekendLift = dow === 5 || dow === 6 ? F.weekendLift : 1
    const baseCents = Math.round((F.baseDailyCents + (d * F.baseDailyStepCents) % F.baseDailyJitterCents) * weekendLift)
    const revenueCents = isPeak ? baseCents + F.peakBonusCents + ((d * F.peakBonusStepCents) % F.peakBonusJitterCents) : baseCents
    const sessions = isPeak ? F.peakSessions + (d % F.peakSessionsJitter) : F.baseSessions + (d % F.baseSessionsJitter)
    const refundsCents = d % 9 === 0 ? 1_200 + (d % 3) * 800 : 0
    const chargebacksCents = d % 14 === 0 ? 3_500 : 0
    days.push({
      date: dateOnlyDaysFromNow(-d),
      revenueCents,
      sessions,
      refundsCents,
      chargebacksCents,
      refundCount: refundsCents > 0 ? 1 : 0,
      netCents: revenueCents - refundsCents,
    })
  }
  // Today is partial — scale the last day down so it reads "in progress".
  const today = days[days.length - 1]
  today.revenueCents = Math.round(today.revenueCents * F.todayPartialFactor)
  today.sessions = Math.round(today.sessions * F.todayPartialFactor)
  today.netCents = today.revenueCents - today.refundsCents
  return days
}

let _daily: Day[] | null = null
function daily(): Day[] {
  if (!_daily) _daily = buildDaily()
  return _daily
}

const sumDaily = (f: (d: Day) => number) => daily().reduce((a, d) => a + f(d), 0)
const sumRows = (rows: Day[], f: (d: Day) => number) => rows.reduce((a, d) => a + f(d), 0)

function reportingDays(from?: string, to?: string): Day[] {
  const start = from ? new Date(`${from}T00:00:00.000Z`) : null
  const end = to ? new Date(`${to}T00:00:00.000Z`) : null
  const valid = start && end
    && Number.isFinite(start.getTime()) && Number.isFinite(end.getTime())
    && start <= end
  if (!valid) return daily()
  const count = Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1
  if (count > 732) return daily()
  const template = daily()
  return Array.from({ length: count }, (_, index) => ({
    ...template[index % template.length]!,
    date: new Date(start.getTime() + index * 86_400_000).toISOString().slice(0, 10),
  }))
}

function zoneRollup(rows: Day[] = daily()) {
  const totalRev = sumRows(rows, d => d.revenueCents)
  const totalSessions = sumRows(rows, d => d.sessions)
  const totalRefunds = sumRows(rows, d => d.refundsCents)
  // Shares are index-aligned to zones[0..2] (primary / overflow / premium).
  return DEMO_ZONES.map((z, i) => {
    const revShare = F.zoneRevShare[i] ?? 0
    const sessShare = F.zoneSessionShare[i] ?? 0
    const revenueCents = Math.round(totalRev * revShare)
    const refundsCents = Math.round(totalRefunds * revShare)
    return {
      zoneId: z.id,
      zoneName: z.name,
      name: z.name,
      revenueCents,
      sessions: Math.round(totalSessions * sessShare),
      avgCents: Math.round((totalRev * revShare) / Math.max(1, totalSessions * sessShare)),
      refundsCents,
      chargebacksCents: 0,
      parkifyFeeCents: Math.round((revenueCents - refundsCents) * PLATFORM_FEE_BPS / 10_000),
      processingCents: 0,
      // Yield denominators, carried through so the demo's "Revenue per space"
      // card shows real per-zone variation (the demo clients define distinct
      // transient/total capacities per zone). Same precedence as the live
      // endpoint: transientCapacity first, totalSpaces as the fallback.
      totalSpaces: z.transientCapacity ?? z.totalSpaces,
    }
  })
}

// ── GET /api/accounting/summary ─────────────────────────────────────────────
export function demoAccountingSummary(options: {
  entityId?: string | null
  restrictedScope?: boolean
  from?: string
  to?: string
  basis?: 'cash' | 'accrual'
} = {}): AccountingSummaryResponse {
  const summaryDays = reportingDays(options.from, options.to)
  const totalRev = sumRows(summaryDays, d => d.revenueCents)
  const totalRefunds = sumRows(summaryDays, d => d.refundsCents)
  const totalCb = sumRows(summaryDays, d => d.chargebacksCents)
  const totalSessions = sumRows(summaryDays, d => d.sessions)
  const net = totalRev - totalRefunds
  const parkifyFeeCents = Math.round(net * PLATFORM_FEE_BPS / 10_000)
  const demoEntityId = `entity-${DEMO_CLIENT.id}`
  const selectedEntity = options.entityId === demoEntityId
  // Whole portfolio: the demo client's every LLC, consolidated (live: entityId=portfolio).
  const portfolio = options.entityId === 'portfolio'
  const exportBlockers: AccountingSourceBlocker[] = [
    ...(selectedEntity || portfolio
      ? options.restrictedScope
        ? [{
            code: 'incomplete_scope' as const,
            message: 'A legal-book export requires the complete client scope with no zone or tenant restriction.',
          }]
        : []
      : [{
          code: 'entity_required' as const,
          message: 'Select exactly one assigned legal entity before exporting accounting data.',
        }]),
  ]
  const range = { from: summaryDays[0]!.date, to: summaryDays[summaryDays.length - 1]!.date }
  if (rangeIncludesOpenMonth(range.from, range.to)) {
    exportBlockers.push({ code: 'open_period', message: OPEN_PERIOD_EXPORT_MESSAGE })
  }
  return {
    range,
    totals: {
      revenueCents: totalRev,
      refundsCents: totalRefunds,
      netCents: net,
      chargebacksCents: totalCb,
      sessionsStarted: totalSessions,
      refundCount: sumRows(summaryDays, d => d.refundCount),
      avgSessionCents: Math.round(totalRev / Math.max(1, totalSessions)),
    },
    // Accounting's byDay rows read `sessionsStarted`; Analytics' daily reads
    // `sessions`. Same series, both field names.
    byDay: summaryDays.map(d => ({ ...d, sessionsStarted: d.sessions })),
    byZone: zoneRollup(summaryDays),
    byEntity: [{
      clientId: DEMO_CLIENT.id, entityId: demoEntityId, entityName: `${DEMO_CLIENT.name} LLC`,
      revenueCents: totalRev, sessions: totalSessions, refundsCents: totalRefunds, chargebacksCents: totalCb, netCents: net,
    }],
    byChannel: F.byChannel.map(c => ({
      channel: c.channel,
      revenueCents: Math.round(totalRev * c.revShare),
      sessions: Math.round(totalSessions * c.sessionShare),
      refundsCents: Math.round(totalRefunds * c.refundShare),
    })),
    takeRate: {
      parkifyFeeCents,
      processingRecoveredCents: 0,
      // A memo, already inside parkifyFeeCents: not subtracted from youKeepCents.
      feeWriteoffCents: Math.round(parkifyFeeCents * 0.012),
      youKeepCents: net - parkifyFeeCents,
      takeRatePctBps: PLATFORM_FEE_BPS,
      takeTxnCount: totalSessions,
      coverageTxnPct: 100,
    },
    // Memo of validation value Parkify covered: not in totals or takeRate.
    parkifyWaivers: { cents: Math.round(totalSessions * 0.004) * 31, chargeCount: Math.round(totalSessions * 0.004) },
    // §3.16 owner statement by fee model — a Model-A bucket (operator bears
    // processing) + a legacy bucket, splitting the totals ~60/40 so the columns
    // sum to the takeRate block above (this screen is demo-intercepted).
    byFeeModel: (() => {
      const aNet = Math.round(net * 0.6), aFee = Math.round(parkifyFeeCents * 0.6)
      const aGross = Math.round(totalRev * 0.6), aRef = Math.round(totalRefunds * 0.6)
      const aTx = Math.round(totalSessions * 0.6)
      return [
        {
          feeModel: 'A', processingAbsorbedBy: 'owner',
          grossCents: aGross, refundsCents: aRef, netCents: aNet,
          yourPayoutCents: aNet - aFee, parkifyFeeCents: aFee, processingRecoveredCents: 0,
          txnCount: aTx, takeTxnCount: aTx,
        },
        {
          feeModel: 'legacy', processingAbsorbedBy: 'parkify',
          grossCents: totalRev - aGross, refundsCents: totalRefunds - aRef, netCents: net - aNet,
          yourPayoutCents: (net - aNet) - (parkifyFeeCents - aFee),
          parkifyFeeCents: parkifyFeeCents - aFee, processingRecoveredCents: 0,
          txnCount: totalSessions - aTx, takeTxnCount: totalSessions - aTx,
        },
      ]
    })(),
    ledgerQuality: {
      available: true,
      readFailed: false,
      truncated: false,
      chargeRowCapHit: false,
      contraEventRowCapHit: false,
    },
    exportReadiness: {
      ready: exportBlockers.length === 0,
      entity: selectedEntity ? {
        clientId: DEMO_CLIENT.id,
        entityId: demoEntityId,
        entityName: `${DEMO_CLIENT.name} LLC`,
      } : null,
      ...(portfolio ? { portfolio: { clientId: DEMO_CLIENT.id, clientName: DEMO_CLIENT.name } } : {}),
      blockers: exportBlockers,
    },
    basis: options.basis ?? 'cash',
    granularity: 'day',
    filters: {
      zones: options.restrictedScope ? [ZONE_MAIN] : [],
      productType: null,
      paymentStatus: null,
      entityId: selectedEntity ? demoEntityId : null,
      unassignedEntityOnly: false,
      ...(portfolio ? { portfolio: true } : {}),
    },
    availableFilters: {
      zones: DEMO_ZONES.map(z => ({ zoneKey: z.id, label: z.name })),
      entities: [{
        entityKey: `${DEMO_CLIENT.id}::${demoEntityId}`,
        clientId: DEMO_CLIENT.id,
        entityId: demoEntityId,
        label: `${DEMO_CLIENT.name} LLC`,
        assignment: 'entity',
      }],
      productTypes: [],
      paymentStatuses: [],
    },
  }
}

// ── Payouts ─────────────────────────────────────────────────────────────────
//
// TWO payout streams, and they are NOT the same money. On a Connect platform
// running destination charges the driver's payment lands on the platform, the
// operator's cut transfers straight out to THEIR connected account, and only
// Parkify's fee stays behind to be paid to Parkify's bank. Sizing both off one
// `payoutRows()` (as this fixture used to) is precisely the conflation that let
// the old reconciliation tab look healthy in demo while reading $0.00 against
// the live platform account.

// Stripe's blended cost on the demo series. Used to size the fee the operator
// bears (Model A: processing rides inside application_fee_amount).
function stripeCostCents(grossCents: number, txnCount: number): number {
  return Math.round(grossCents * 0.029) + txnCount * 30
}

function weeklyPayouts(weeklyCents: number, idPrefix: string): Payout[] {
  return Array.from({ length: 6 }, (_, k) => {
    const daysAgo = k * 7 + 2
    const amount = Math.round(weeklyCents * (0.9 + ((k * 7) % 5) / 20))
    return {
      id: `${idPrefix}_${String(k).padStart(3, '0')}`,
      amount,
      arrival_date: unixDaysFromNow(-daysAgo + 2),
      created: unixDaysFromNow(-daysAgo),
      currency: 'usd',
      status: 'paid',
      type: 'bank_account',
      method: k === 0 ? 'instant' : 'standard',
      description: 'PARKIFY PAYMENTS',
      statement_descriptor: 'PARKIFY',
      failure_message: null,
    }
  })
}

// Six weekly rows sized to sum to the period total, so the payout tables tie
// back to the statement above them instead of overshooting it by the 6-weeks-
// of-rows / 30-days-of-activity gap.
const PAYOUT_WEEKS = 6

/** What reaches PARKIFY's bank: the platform fee, less Stripe's cost. */
function platformPayoutRows(): Payout[] {
  const gross = sumDaily(d => d.revenueCents)
  const fee = Math.round(gross * (PLATFORM_FEE_BPS / 10000))
  return weeklyPayouts(Math.round(fee / PAYOUT_WEEKS), 'po_demo')
}

/** What reaches the OPERATOR's bank: everything except Parkify's fee. */
function operatorPayoutRows(): Payout[] {
  const gross = sumDaily(d => d.revenueCents)
  const refunds = sumDaily(d => d.refundsCents)
  const fee = Math.round(gross * (PLATFORM_FEE_BPS / 10000))
  const processing = stripeCostCents(gross, sumDaily(d => d.sessions))
  return weeklyPayouts(Math.round((gross - refunds - fee - processing) / PAYOUT_WEEKS), 'po_demo_op')
}

export function demoPayouts(): PayoutListResponse {
  return { data: platformPayoutRows(), has_more: false }
}

export function demoPayoutDetail(id: string): PayoutDetailResponse {
  const rows = platformPayoutRows()
  const payout = rows.find(p => p.id === id) ?? rows[0]
  // Derive the drilldown FROM the payout so it actually reconciles — a fixed
  // transaction list would print a permanent "Mismatch" the moment the payout
  // sizing changed.
  const COUNT = 8
  const perNet = Math.round(payout.amount / COUNT)
  const transactions = Array.from({ length: COUNT }, (_, k) => {
    const fee = Math.round(perNet * 0.029) + 30
    const amount = perNet + fee
    const zone = DEMO_ZONES[k % DEMO_ZONES.length]
    return {
      id: `txn_bal_${id}_${k}`,
      amount,
      net: perNet,
      fee,
      currency: 'usd',
      type: 'charge',
      description: 'Parking session',
      created: payout.created - k * 3600,
      available_on: payout.arrival_date,
      source: {
        id: `ch_demo_${id}_${k}`,
        object: 'charge',
        amount,
        metadata: { plate: DEMO_PLATES[k % DEMO_PLATES.length], zoneId: zone.id },
        billing_details: { email: DEMO_EMAILS[k % DEMO_EMAILS.length], name: null },
        receipt_email: DEMO_EMAILS[k % DEMO_EMAILS.length],
      },
      // What the Worker's attributeZones resolves per row (see payouts.ts).
      parkifyZoneId: zone.id,
      parkifyZoneName: zone.name,
    }
  })
  // Rolled up FROM the transactions for the same reason the reconciliation is —
  // a hand-written breakdown would stop summing to the payout the moment the
  // fixture's sizing changed.
  const byZone = DEMO_ZONES.map(z => {
    const rows = transactions.filter(t => t.parkifyZoneId === z.id)
    return {
      zoneId: z.id,
      zoneName: z.name,
      netCents: rows.reduce((a, t) => a + t.net, 0),
      grossCents: rows.reduce((a, t) => a + t.amount, 0),
      feeCents: rows.reduce((a, t) => a + t.fee, 0),
      txnCount: rows.length,
    }
  })
    .filter(z => z.txnCount > 0)
    .sort((a, b) => b.netCents - a.netCents)
  const charges = transactions.reduce((a, t) => a + t.amount, 0)
  // POSITIVE, matching getPayoutDetails (`fees += t.fee`). The screen renders
  // this line as `−{fees}`, so a negative here printed "−-$220.16".
  const fees = transactions.reduce((a, t) => a + t.fee, 0)
  const net = charges - fees
  return {
    payout,
    transactions,
    has_more: false,
    // Every demo payout is a standard automatic one, so it itemizes. The
    // un-itemizable shapes (auto-debit / manual / instant) are a real-Stripe
    // condition; a demo has no reason to show a bank withdrawal to a prospect.
    itemization: { available: true },
    byZone,
    zoneLookupTruncated: false,
    // Demo zones resolve straight from the batch, so no lookups are issued and
    // none can fail — the degrade warning stays off in a demo.
    zoneLookup: { attempted: 0, notFound: 0, errored: 0 },
    reconciliation: {
      charges,
      refunds: 0,
      fees,
      adjustments: 0,
      net,
      payoutAmount: payout.amount,
      delta: payout.amount - net,
      // The fixture carries no sweep row, so there is nothing excluded here.
      payoutRowNetCents: 0,
      matched: Math.abs(payout.amount - net) < 100,
    },
  }
}

// ── Money-flow statement (GET /api/accounting/reconciliation) ───────────────
//
// Built from the SAME daily series as the summary, then run through the real
// destination-charge math so the demo shows the shape a live Connect platform
// actually has: gross lands on the platform, the operator's cut transfers out,
// Parkify keeps the application fee. A flat "payouts = revenue" fixture would
// hide exactly the layout the live account exposes.
export function demoReconciliation(from: string, to: string): ReconciliationResponse {
  const grossCents = sumDaily(d => d.revenueCents)
  const refundsCents = sumDaily(d => d.refundsCents)
  const txnCount = sumDaily(d => d.sessions)
  const stripeFeeCents = stripeCostCents(grossCents, txnCount)
  const parkifyFeeCents = Math.round(grossCents * (PLATFORM_FEE_BPS / 10000))

  // Model A, the shape GA335 actually runs: the operator bears Stripe's cost,
  // so processing rides inside application_fee_amount and the transfer out is
  // gross − refunds − fee − processing. What stays behind on the platform is
  // then Parkify's real take, which is what the statement's "retained" line
  // must equal.
  const transferredOutCents = grossCents - refundsCents - parkifyFeeCents - stripeFeeCents
  const retainedCents = grossCents - stripeFeeCents - refundsCents - transferredOutCents
  const platformPayouts = platformPayoutRows()
  const payoutsCents = platformPayouts.reduce((s, p) => s + p.amount, 0)
  const operatorPaidCents = operatorPayoutRows().reduce((s, p) => s + p.amount, 0)

  return {
    range: { from, to },
    currency: 'usd',
    scoped: false,
    platform: {
      grossCents,
      stripeFeeCents,
      refundsCents,
      transferredOutCents,
      applicationFeesCents: 0,
      adjustmentsCents: 0,
      otherCents: 0,
      retainedCents,
      payoutsCents,
      payoutCount: platformPayouts.length,
      zeroPayoutCount: 0,   // the demo platform's schedule always has something to send
      txnCount: txnCount * 2 + platformPayouts.length,   // charge + transfer per session
      truncated: false,
      availableCents: Math.max(0, retainedCents - payoutsCents),
      pendingCents: Math.round(parkifyFeeCents / 8),
    },
    platformError: null,
    entities: [{
      clientId: DEMO_CLIENT.id,
      clientName: DEMO_CLIENT.name,
      entityId: 'ent_demo_0001',
      entityName: `${DEMO_CLIENT.name} LLC`,
      stripeAccountId: 'acct_demo0001',
      payoutsCents: operatorPaidCents,
      payoutCount: 6,
      lastPayoutArrival: unixDaysFromNow(-2),
      pendingPayoutsCents: 0,
      availableCents: Math.max(0, transferredOutCents - operatorPaidCents),
      pendingCents: Math.round(transferredOutCents * 0.04),
      error: null,
    }],
    entitiesSkipped: 0,
    ledger: {
      grossCents,
      refundsCents,
      netCents: grossCents - refundsCents,
      parkifyFeeCents,
      ownerTransferCents: transferredOutCents,
      processingRecoveredCents: stripeFeeCents,
      txnCount,
      hasTransferData: true,
    },
    tieOut: {
      grossStripeCents: grossCents,
      grossLedgerCents: grossCents,
      // No boundary rows in the fixture: the demo series is built from whole
      // local days, so the operator's window and Stripe's UTC window cover the
      // same sessions and the reconciling line correctly doesn't render. The
      // boundary case is a real-clock condition, not something to stage.
      grossLedgerComparableCents: grossCents,
      boundaryCents: 0,
      boundaryRowCount: 0,
      boundaryTxnIds: [],
      grossDeltaCents: 0,
      transfersStripeCents: transferredOutCents,
      transfersLedgerCents: transferredOutCents,
      transfersLedgerComparableCents: transferredOutCents,
      transfersDeltaCents: 0,
      transfersComparable: true,
      matched: true,
    },
  }
}

// ── Analytics (GET /api/analytics/business) ─────────────────────────────────
export function demoAnalytics(requestedZoneName = '', requestedZoneIds: string[] = []) {
  const allZones = zoneRollup()
  const hasFocus = requestedZoneIds.length > 0 || !!requestedZoneName
  const selectedZones = requestedZoneIds.length
    ? allZones.filter(zone => requestedZoneIds.includes(zone.zoneId))
    : allZones.filter(zone => zone.name === requestedZoneName || zone.zoneId === requestedZoneName)
  const selectedIndices = selectedZones.map(zone => DEMO_ZONES.findIndex(z => z.id === zone.zoneId))
  const revShare = hasFocus ? selectedIndices.reduce((sum, i) => sum + (F.zoneRevShare[i] ?? 0), 0) : 1
  const sessionShare = hasFocus ? selectedIndices.reduce((sum, i) => sum + (F.zoneSessionShare[i] ?? 0), 0) : 1
  const sourceDays = daily()
  const zoneSeries = selectedIndices.map(i => sourceDays.map(d => ({
    date: d.date,
    revenueCents: Math.round(d.revenueCents * (F.zoneRevShare[i] ?? 0)),
    sessions: Math.round(d.sessions * (F.zoneSessionShare[i] ?? 0)),
  })))
  const series = hasFocus
    ? sourceDays.map((d, day) => ({
        date: d.date,
        revenueCents: zoneSeries.reduce((sum, rows) => sum + rows[day].revenueCents, 0),
        sessions: zoneSeries.reduce((sum, rows) => sum + rows[day].sessions, 0),
      }))
    : sourceDays.map(d => ({ date: d.date, revenueCents: d.revenueCents, sessions: d.sessions }))
  const totalRev = series.reduce((sum, d) => sum + d.revenueCents, 0)
  const totalSessions = series.reduce((sum, d) => sum + d.sessions, 0)
  const totalRefunds = Math.round(sumDaily(d => d.refundsCents) * revShare)
  // Sum rounded daily rows once so the selected-zone cards and trend agree.
  const z = hasFocus
    ? selectedZones.map((zone, i) => ({
        ...zone,
        revenueCents: zoneSeries[i].reduce((sum, d) => sum + d.revenueCents, 0),
        sessions: zoneSeries[i].reduce((sum, d) => sum + d.sessions, 0),
        avgCents: Math.round(zoneSeries[i].reduce((sum, d) => sum + d.revenueCents, 0) / Math.max(1, zoneSeries[i].reduce((sum, d) => sum + d.sessions, 0))),
      }))
    : allZones
  const permitCounts = { active: Math.round(F.permits.active * sessionShare), expiringSoon: Math.round(F.permits.expiringSoon * sessionShare), expired: Math.round(F.permits.expired * sessionShare) }
  const validatedSessions = Math.min(Math.round(F.validations.sessionsWithDiscount * sessionShare), totalSessions)
  const compedSessions = Math.min(Math.round(totalSessions * 0.02), totalSessions - validatedSessions)
  const paidSessions = Math.max(0, totalSessions - validatedSessions - compedSessions)
  return {
    range: { from: dateOnlyDaysFromNow(-29), to: dateOnlyDaysFromNow(0) },
    selectedZoneIds: hasFocus ? selectedZones.map(zone => zone.zoneId) : [],
    availableZones: allZones.map(zone => ({ zoneId: zone.zoneId, name: zone.name })),
    kpis: {
      revenueCents: totalRev,
      sessionsStarted: totalSessions,
      avgSessionCents: Math.round(totalRev / Math.max(1, totalSessions)),
      avgDurationMin: F.kpis.avgDurationMin,
      refundsCents: totalRefunds,
      refundRate: totalRefunds / Math.max(1, totalRev),
      chargebacksCents: Math.round(sumDaily(d => d.chargebacksCents) * revShare),
      activeNow: Math.round(F.kpis.activeNow * sessionShare),
      activeReservedCents: Math.round(F.kpis.activeReservedCents * revShare),
    },
    daily: series,
    // Keep the demo's focused trend on the same source series as the global
    // trend and top-zone rollup. The live endpoint returns an empty zoneIds
    // array when the requested name is unknown; mirror that contract instead
    // of making an unrecognized focus look like real zero activity.
    zoneTrend: hasFocus
      ? {
          zoneName: selectedZones.length ? selectedZones.map(zone => zone.name).join(', ') : requestedZoneName || 'Selected zones',
          zoneIds: selectedZones.map(zone => zone.zoneId),
          daily: selectedZones.length ? series : [],
        }
      : null,
    topZones: z.map(zz => ({ zoneId: zz.zoneId, name: zz.name, revenueCents: zz.revenueCents, sessions: zz.sessions, avgCents: zz.avgCents })),
    zoneYield: (() => {
      const days = Math.max(daily().length, 1)
      const rows = z
        .map(zz => ({
          zoneId: zz.zoneId,
          name: zz.name,
          totalSpaces: zz.totalSpaces,
          revenueCents: zz.revenueCents,
          sessions: zz.sessions,
          revenuePerSpacePerDayCents: zz.totalSpaces
            ? Math.round(zz.revenueCents / zz.totalSpaces / days)
            : undefined,
          sessionsPerSpacePerDay: zz.totalSpaces
            ? Number((zz.sessions / zz.totalSpaces / days).toFixed(2))
            : undefined,
        }))
        .sort((a, b) => (b.revenuePerSpacePerDayCents ?? -1) - (a.revenuePerSpacePerDayCents ?? -1))
      return {
        days,
        rows,
        zonesMissingCapacity: rows.filter(r => r.totalSpaces == null).length,
      }
    })(),
    byFacility: F.byFacility.filter(f => !hasFocus || selectedZones.some(zone => DEMO_ZONES.find(z => z.id === zone.zoneId)?.facilityId === f.facilityId)).map(f => ({
      facilityId: f.facilityId,
      facilityName: f.facilityName,
      revenueCents: hasFocus ? z.filter(zone => DEMO_ZONES.find(d => d.id === zone.zoneId)?.facilityId === f.facilityId).reduce((sum, zone) => sum + zone.revenueCents, 0) : Math.round(totalRev * f.revShare),
      sessions: hasFocus ? z.filter(zone => DEMO_ZONES.find(d => d.id === zone.zoneId)?.facilityId === f.facilityId).reduce((sum, zone) => sum + zone.sessions, 0) : Math.round(totalSessions * f.sessionShare),
      zones: hasFocus ? selectedZones.filter(zone => DEMO_ZONES.find(d => d.id === zone.zoneId)?.facilityId === f.facilityId).length : f.zones,
    })),
    durationMix: F.durationMix.map(d => ({ bucket: d.bucket, sessions: Math.round(totalSessions * d.share) })),
    sessionMix: {
      trackedSessions: totalSessions,
      rows: [
        { type: 'paid' as const, sessions: paidSessions, revenueCents: Math.round(totalRev * paidSessions / Math.max(1, totalSessions)) },
        { type: 'validated' as const, sessions: validatedSessions, revenueCents: Math.round(totalRev * validatedSessions / Math.max(1, totalSessions)) },
        { type: 'comped' as const, sessions: compedSessions, revenueCents: 0 },
      ],
      permit: { activePermits: permitCounts.active, sessionsTracked: false as const },
    },
    hourOfDay: F.hourOfDay.map(n => Math.round(n * sessionShare)),
    dayOfWeek: F.dayOfWeek.map(n => Math.round(n * sessionShare)),
    retention: {
      uniquePlates: Math.round(totalSessions * F.retention.uniqueShare),
      returningPlates: Math.round(totalSessions * F.retention.returningShare),
      returnRate: F.retention.returnRate,
    },
    validations: { totalCodesActive: Math.round(F.validations.totalCodesActive * sessionShare), sessionsWithDiscount: validatedSessions },
    permits: permitCounts,
  }
}

// ── Disputes (GET /api/disputes) ────────────────────────────────────────────
function makeDispute(opts: {
  id: string; amount: number; reason: string; status: DisputeStatus
  createdDaysAgo: number; dueDaysFromNow: number; plate: string; email: string
  zoneId: string; refundable: boolean
}): DisputeResponse {
  return {
    id: opts.id,
    amount: opts.amount,
    created: unixDaysFromNow(-opts.createdDaysAgo),
    currency: 'usd',
    payment_intent: {
      id: `pi_${opts.id}`,
      metadata: { plate: opts.plate, zoneId: opts.zoneId },
      receipt_email: opts.email,
      latest_charge: {
        id: `ch_${opts.id}`,
        receipt_email: opts.email,
        billing_details: { email: opts.email, name: null },
      },
    },
    charge: `ch_${opts.id}`,
    reason: opts.reason,
    status: opts.status,
    evidence: {
      customer_email_address: opts.email,
      product_description: `Parking session — ${zoneName(opts.zoneId)}`,
    },
    evidence_details: {
      due_by: unixDaysFromNow(opts.dueDaysFromNow),
      has_evidence: opts.status !== 'needs_response',
      past_due: false,
      submission_count: opts.status === 'needs_response' ? 0 : 1,
    },
    is_charge_refundable: opts.refundable,
    metadata: { plate: opts.plate, zoneId: opts.zoneId },
    network_reason_code: '4855',
  }
}

function disputeRows() {
  return [
    makeDispute({ id: 'dp_demo_001', amount: 3500, reason: 'fraudulent', status: 'needs_response', createdDaysAgo: 2, dueDaysFromNow: 5, plate: DEMO_PLATES[3], email: DEMO_EMAILS[3], zoneId: ZONE_MAIN, refundable: true }),
    makeDispute({ id: 'dp_demo_002', amount: 6000, reason: 'product_not_received', status: 'under_review', createdDaysAgo: 9, dueDaysFromNow: 3, plate: DEMO_PLATES[7], email: DEMO_EMAILS[7], zoneId: ZONE_VIP, refundable: false }),
    makeDispute({ id: 'dp_demo_003', amount: 2500, reason: 'duplicate', status: 'won', createdDaysAgo: 26, dueDaysFromNow: -10, plate: DEMO_PLATES[1], email: DEMO_EMAILS[1], zoneId: ZONE_NORTH, refundable: false }),
    makeDispute({ id: 'dp_demo_004', amount: 4000, reason: 'fraudulent', status: 'lost', createdDaysAgo: 40, dueDaysFromNow: -10, plate: DEMO_PLATES[5], email: DEMO_EMAILS[5], zoneId: ZONE_NORTH, refundable: false }),
  ]
}

// Stored deadline + alert state (api/src/payments/dispute_tracking.ts): the
// open one has had its "opened" alert; the lost one's recovery is not taken.
function demoTracking(d: DisputeResponse): DisputeTracking | null {
  const at = (days: number) => new Date(unixDaysFromNow(days) * 1000).toISOString()
  const base = { dueBy: d.evidence_details.due_by ? new Date(d.evidence_details.due_by * 1000).toISOString() : null,
    updatedAt: at(0), source: 'sweep', recoveryState: null, recoveryCents: null }
  const none = { opened: null, due72h: null, due24h: null, recovery: null }
  if (d.id === 'dp_demo_001') return { ...base, alerts: { ...none, opened: at(-2) } }
  if (d.id === 'dp_demo_002') return { ...base, alerts: { ...none, opened: at(-9), due72h: at(-1) } }
  if (d.id === 'dp_demo_004') return { ...base, alerts: { ...none, opened: at(-40), recovery: at(-3) }, recoveryState: 'pending', recoveryCents: 3230 }
  return null
}

export function demoDisputes(): DisputesListResponse {
  return { data: disputeRows().map(d => ({ ...d, tracking: demoTracking(d) })), has_more: false }
}
// Lost-dispute recovery plan (api/src/payments/dispute_recovery.ts): a $40.00
// charge (the transfer is the full $40; $5.40 Parkify fee comes back), $20 of
// it disputed, $15 fee, not yet recovered.
export function demoDisputeRecovery(id: string) {
  const d = demoDisputeById(id)
  if (d.status !== 'lost') return { plan: { kind: 'none', reason: 'not_lost', disputeId: d.id } }
  return { plan: {
    kind: 'reverse', disputeId: d.id, chargeId: d.charge, transferId: 'tr_demo_004', destination: 'acct_demo_client',
    chargeCents: d.amount, disputedCents: 2000, transferCents: 4000, applicationFeeCents: 540, alreadyReversedCents: 0,
    clientShareCents: 1730, disputeFeeCents: 1500, recoverCents: { client: 3230, parkify: 1730 },
    idempotencyKey: `dispute-recover:${d.id}`,
  } }
}

export function demoDisputeById(id: string): DisputeResponse {
  return disputeRows().find(d => d.id === id) ?? disputeRows()[0]
}

// ── Client Admin Stripe panels ──────────────────────────────────────────────
// The operator's OWN connected account — their cut, not Parkify's fee.
export function demoStripePayouts() {
  return { data: operatorPayoutRows() }
}

/** GET /api/clients/:id/entities — the demo client is one LLC on one account. */
export function demoPayoutEntities() {
  return {
    entities: [{
      id: 'ent_demo_main', clientId: DEMO_CLIENT.id, name: `${DEMO_CLIENT.name} LLC`, active: true,
      contactEmail: DEMO_CLIENT.contactEmail, stripeAccountId: DEMO_CLIENT.stripeAccountId,
      onboardingStatus: 'charges_enabled', chargesEnabled: true, payoutsEnabled: true,
      requirementsCurrentlyDue: [], onboardingStatusUpdatedAt: isoDaysFromNow(-45),
    }],
    defaultPayoutEntityId: 'ent_demo_main',
  }
}

/**
 * GET /api/accounting/payout-statements/:id — built so the lines add up to the
 * deposit exactly, like the real statement. The instant payout (k=0) is the one
 * Stripe will not itemize, so the demo also shows the incomplete banner.
 */
export function demoPayoutStatement(id: string): PayoutStatement {
  const rows = operatorPayoutRows()
  const p = rows.find(r => r.id === id) ?? rows[1]!
  const k = Number(p.id.slice(-3)) || 0
  const instant = p.method === 'instant'
  const zones = [ZONE_MAIN, ZONE_NORTH, ZONE_VIP]
  const lines: PayoutStatementLine[] = []
  // One parking charge: Parkify fee at the contract rate, card processing at 2.9% + 30¢.
  const charge = (n: number, gross: number): PayoutStatementLine => {
    const parkify = Math.round(gross * (PLATFORM_FEE_BPS / 10000))
    const card = Math.round(gross * 0.029) + 30
    const zoneId = zones[n % zones.length]!
    return {
      balanceTxnId: `txn_demo_${k}_${n}`, kind: 'charge', stripeType: 'payment',
      chargedAt: isoDaysFromNow(-(k * 7 + 3) - (n % 6) / 10), sessionTxnId: `txn_demo_${k}${String(n).padStart(3, '0')}`,
      zoneId, zoneName: zoneName(zoneId), grossCents: gross,
      parkifyFeeCents: parkify, cardProcessingCents: card, taxHeldCents: 0, combinedFeesCents: 0,
      netCents: gross - parkify - card,
    }
  }
  if (!instant) {
    let left = p.amount
    const sizes = [1800, 2400, 1200, 3600, 900, 4800]
    for (let n = 0; left > 0; n++) {
      const l = charge(n, sizes[n % sizes.length]!)
      if (l.netCents >= left) {
        // Last charge carries what is left, so Σ net is the deposit to the cent.
        const parkify = Math.min(left, l.parkifyFeeCents)
        lines.push({ ...l, grossCents: left + parkify, parkifyFeeCents: parkify, cardProcessingCents: 0, netCents: left })
        break
      }
      lines.push(l)
      left -= l.netCents
      if (n === 2) {
        // A refund on the second charge, and the fee Stripe returned on it.
        const r = lines[1]!
        lines.push({ ...r, balanceTxnId: `${r.balanceTxnId}_r`, kind: 'refund', stripeType: 'payment_refund',
          grossCents: -r.grossCents, parkifyFeeCents: 0, cardProcessingCents: 0, netCents: -r.grossCents })
        lines.push({ ...r, balanceTxnId: `${r.balanceTxnId}_f`, kind: 'fee_returned', stripeType: 'adjustment',
          grossCents: r.parkifyFeeCents, parkifyFeeCents: 0, cardProcessingCents: 0, netCents: r.parkifyFeeCents })
        left += r.grossCents - r.parkifyFeeCents
      }
    }
  }
  const sum = (f: (l: PayoutStatementLine) => number, only?: (l: PayoutStatementLine) => boolean) =>
    lines.reduce((a, l) => a + (only && !only(l) ? 0 : f(l)), 0)
  const linesNet = sum(l => l.netCents)
  return {
    payout: {
      id: p.id, clientId: DEMO_CLIENT.id, entityId: 'ent_demo_main', amountCents: p.amount, currency: 'usd',
      status: p.status, arrivalDate: new Date((p.arrival_date ?? 0) * 1000).toISOString().slice(0, 10),
      createdAt: new Date((p.created ?? 0) * 1000).toISOString(),
    },
    complete: !instant,
    gaps: instant ? ['itemization_unavailable', 'lines_do_not_sum'] : [],
    totals: {
      grossSalesCents: sum(l => l.grossCents, l => l.kind === 'charge'),
      refundsAndChargebacksCents: sum(l => l.grossCents, l => l.kind === 'refund' || l.kind === 'chargeback'),
      parkifyFeeCents: sum(l => l.parkifyFeeCents),
      cardProcessingCents: sum(l => l.cardProcessingCents),
      taxHeldCents: 0,
      combinedFeesCents: 0,
      adjustmentsCents: sum(l => l.grossCents, l => l.kind === 'fee_returned' || l.kind === 'adjustment' || l.kind === 'other'),
      linesNetCents: linesNet,
      payoutCents: p.amount,
      unexplainedCents: p.amount - linesNet,
    },
    lines,
  }
}
export function demoInstantEligibility() {
  return {
    eligible: true,
    instantAvailableCents: F.payoutInstant.instantAvailableCents,
    currency: 'usd',
    feePct: 1.5,
    estFeeCents: F.payoutInstant.estFeeCents,
    externalAccountLast4: F.payoutInstant.externalAccountLast4,
    reason: null,
  }
}

// ── Subscriptions (GET /api/subscriptions) ──────────────────────────────────
//
// Added 2026-08-13, when Subscriptions became its own top-level screen. It was
// previously a tab inside the Parkify-staff-only Customers screen, so the demo's
// client-scoped persona could never reach it and it never needed a fixture —
// route parity carried it as a documented TODO. The moment it became reachable,
// "renders empty until fixtured" turned into a blank table on a live prospect
// call, so it gets real rows.
//
// Plan id/label track the zone's own monthlyPlans (demo/data/zones.ts plansFor)
// so the Subscriptions list and the zone's rates card can't disagree on screen.
// Statuses are deliberately mixed: a healthy book has a past_due and a
// cancel-at-period-end in it, and those two rows are the ones whose rendering is
// worth showing (and worth catching if it regresses).
export function demoSubscriptions() {
  const plan = (zoneId: string) => `${zoneId}-plan`
  const label = DEMO_CLIENT.recurringPlanLabel
  // Priced off the zone's own type, the same input plansFor uses — so a zone
  // whose type changes can't leave this list quoting a stale amount.
  const cents = (zoneId: string) => {
    const z = DEMO_ZONES.find(x => x.id === zoneId) ?? DEMO_ZONES[0]
    return DEMO_CLIENT.monthlyPlanCentsByType[z.type]
  }
  const row = (
    n: number, zoneId: string,
    status: string, over: Record<string, unknown> = {},
  ) => ({
    id: `sub_demo_${String(n).padStart(3, '0')}`,
    zoneId,
    planId: plan(zoneId),
    planLabel: label,
    amountCents: cents(zoneId),
    plate: DEMO_PLATES[n % DEMO_PLATES.length],
    email: DEMO_EMAILS[n % DEMO_EMAILS.length],
    status,
    stripeSubscriptionId: `sub_demo${n}`,
    stripeCustomerId: `cus_demo${n}`,
    startedAt: isoDaysFromNow(-120 - n * 11),
    currentPeriodEnd: isoDaysFromNow(30 - (n % 20)),
    createdAt: isoDaysFromNow(-120 - n * 11),
    updatedAt: isoDaysFromNow(-(n % 9)),
    invoiceCount: 3 + (n % 7),
    ...over,
  })

  return [
    row(0, ZONE_MAIN, 'active'),
    row(1, ZONE_MAIN, 'active'),
    row(2, ZONE_NORTH, 'active'),
    // Card expired last cycle — the row an operator actually needs to act on.
    row(3, ZONE_MAIN, 'past_due'),
    row(4, ZONE_VIP, 'active'),
    // Still active through the paid period, but won't renew.
    row(5, ZONE_NORTH, 'active', { cancelAtPeriodEnd: true }),
    row(6, ZONE_MAIN, 'active'),
    row(7, ZONE_VIP, 'canceled', {
      canceledAt: isoDaysFromNow(-16), currentPeriodEnd: isoDaysFromNow(-16),
    }),
  ]
}

// GET /api/subscriptions/<id> → the row plus its billing history.
//
// Derived from demoSubscriptions() rather than authored separately: the modal
// opens FROM a row, so a detail record that disagreed with the list it came from
// would be visible on screen in the same click.
export function demoSubscriptionById(id: string) {
  const sub = demoSubscriptions().find(s => s.id === id) ?? demoSubscriptions()[0]
  // One paid invoice per elapsed month, newest first. A past_due subscription's
  // most recent invoice is the one still open — that's what makes the status
  // legible in the modal rather than just a badge.
  const invoices = Array.from({ length: sub.invoiceCount }, (_, i) => {
    const open = i === 0 && sub.status === 'past_due'
    return {
      id: `in_${sub.id}_${i}`,
      number: `${sub.id.slice(-3).toUpperCase()}-${String(sub.invoiceCount - i).padStart(4, '0')}`,
      amountPaidCents: open ? 0 : sub.amountCents,
      amountDueCents: sub.amountCents,
      status: open ? 'open' : 'paid',
      createdAt: isoDaysFromNow(-i * 30),
      periodStart: isoDaysFromNow(-i * 30),
      periodEnd: isoDaysFromNow(-i * 30 + 30),
    }
  })
  return { ...sub, invoices }
}

// Parkify-internal platform analytics. The fixture deliberately includes a
// concentrated client mix, partial Stripe-fee coverage, and a small pricing
// loss so the dashboard's decision signals are visible in demo mode.
export function demoPlatformAnalytics(): PlatformTakeRateResponse {
  const now = new Date()
  const byPeriod = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(now); d.setDate(d.getDate() - (29 - i))
    const netTakeCents = [1200, 1850, 900, 2500, 3100, 1600, 4300][i % 7] + i * 85
    const grossTpvCents = Math.round(netTakeCents / 0.1392)
    return { date: d.toISOString().slice(0, 10), grossTpvCents, netTakeCents, takeRatePctBps: 1392 }
  })
  const byClient = [
    { clientId: 'demo-orlando', clientName: 'Parking Orlando LLC', grossTpvCents: 452_263, netTakeCents: 70_260, takeRatePctBps: 1553, txnCount: 383 },
    { clientId: 'demo-national', clientName: 'National Parking', grossTpvCents: 255_586, netTakeCents: 39_280, takeRatePctBps: 1537, txnCount: 405 },
    { clientId: 'demo-gulf', clientName: 'Sir Gus Consulting LLC', grossTpvCents: 15_033, netTakeCents: 2_583, takeRatePctBps: 1718, txnCount: 21 },
    { clientId: 'demo-peaks', clientName: 'Four Peaks Holdings LLC', grossTpvCents: 22_332, netTakeCents: 0, takeRatePctBps: 0, txnCount: 57 },
  ]
  const byZone: NonNullable<PlatformTakeRateResponse['byZone']> = byClient.map((c, i) => ({
    zoneId: `demo-zone-${i}`, zoneName: ['FL9', 'GA18', 'TX1340', 'CA1120'][i], clientId: c.clientId, clientName: c.clientName,
    settlement: i < 3 ? 'connect' : 'direct', grossTpvCents: c.grossTpvCents, netTakeCents: c.netTakeCents,
    processingRecoveredCents: i === 0 ? 12_400 : 0, settledToParkifyCents: i === 3 ? c.grossTpvCents : c.netTakeCents,
    takeRatePctBps: c.takeRatePctBps, txnCount: c.txnCount,
  }))
  return {
    range: { from: byPeriod[0].date, to: byPeriod[29].date }, basis: 'cash', granularity: 'day',
    totals: { grossTpvCents: 805_471, refundsCents: 0, netTpvCents: 805_471, netTakeCents: 112_123, stripeCostEstCents: 32_390, netAfterStripeCents: 79_733, takeRatePctBps: 1392, txnCount: 866, takeTxnCount: 809, coverageTxnPct: 93, ownerTransferCents: 661_572, parkifyNetCents: 112_123, stripeFeeCents: 31_776, feeCoveragePct: 96 },
    byPeriod, byClient, byEntity: [], byZone,
    byFeeModel: [
      { feeModel: 'B', processingAbsorbedBy: 'parkify', driverTotalCents: 452_263, ownerTransferCents: 350_000, parkifyTakeCents: 70_260, stripeFeeCents: 32_003, txnCount: 383, feeCoveragePct: 98, reconciledOk: true, negativeMarginCents: 120, negativeMarginTxns: 3 },
      { feeModel: 'A', processingAbsorbedBy: 'owner', driverTotalCents: 353_208, ownerTransferCents: 311_572, parkifyTakeCents: 41_863, stripeFeeCents: 0, txnCount: 483, feeCoveragePct: 94, reconciledOk: true, negativeMarginCents: 0, negativeMarginTxns: 0 },
    ],
    generatedAt: new Date().toISOString(),
  }
}

/** Avery's resolution rate per channel for the last complete ISO week (docs/AVERY-RESOLUTION-RATE.md). */
export function demoAveryResolutionRate() {
  const now = new Date()
  const day = (now.getUTCDay() + 6) % 7
  const thisMonday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - day * 86_400_000
  const weekStart = new Date(thisMonday - 7 * 86_400_000).toISOString().slice(0, 10)
  const weekEnd = new Date(thisMonday).toISOString().slice(0, 10)
  const row = (channel: string, resolved: number, denominator: number, escalated: number, breakdown: Record<string, number>, note: string | null = null) =>
    ({ channel, resolved, denominator, rate: denominator ? resolved / denominator : null, escalated, breakdown, complete: true, note })
  return {
    available: true, definitionsVersion: 1, docPath: 'docs/AVERY-RESOLUTION-RATE.md',
    weekStart, weekEnd, computedAt: new Date(thisMonday + 4 * 3_600_000 + 17 * 60_000).toISOString(),
    platform: [
      row('email', 41, 52, 6, { held_for_person: 3, corrected_by_person: 2 }),
      row('sms', 18, 23, 5, { awaiting_parker: 4, unanswered: 1 }),
      row('voice', 9, 17, 3, { person_resolved: 2, abandoned: 3, excluded_voicemail: 1, excluded_test_calls: 2 }),
      row('web', 27, 31, 0, { handed_to_person: 4, excluded_eval: 2 }, 'chats the visitor left without an answer or details are not recorded'),
    ],
    clients: [
      { clientId: 'client_demo_001', rows: [row('email', 12, 15, 2, {}), row('sms', 6, 7, 1, {}), row('voice', 3, 5, 1, { abandoned: 1 })] },
      { clientId: 'client_demo_002', rows: [row('email', 9, 11, 1, { held_for_person: 1 }), row('sms', 4, 5, 1, {}), row('voice', 2, 4, 1, { person_resolved: 1 })] },
    ],
    operators: [{ operatorSlug: 'national', rows: [row('email', 7, 9, 1, { held_for_person: 1 }), row('voice', 2, 5, 1, { abandoned: 2 })] }],
    unattributed: [row('email', 13, 17, 2, { held_for_person: 1, corrected_by_person: 1 }), row('sms', 8, 11, 3, {}), row('voice', 2, 3, 0, { person_resolved: 1 })],
    unavailable: [row('web', 0, 0, 0, {}, 'the zone a visitor typed is text, never parsed; not attributable per client')],
  }
}

export function demoAutonomousOpsCost() {
  return { available: true, windowHours: 720, activeOperators: 9, costUsd: 181.81, interactions: 12_482, ticketCount: 448, escalationCount: 26, escalationRate: .058, tokens: { total: 4_820_000 }, llmTurnCostUsd: 92.14, ledgerCostUsd: 89.67, fixedCostUsd: 13.77, variableCostUsd: 168.04, coverage: { metered_usd: 142.66, invoice_usd: 39.15, metered_share: .785 }, monthlyFixedCostPerOperatorUsd: 1.53, monthlyVariableCostPerOperatorUsd: 18.67, monthlyCostUsd: 181.81, costPerOperatorUsd: 20.20, monthlyCostPerOperatorUsd: 20.20, costPerTicketUsd: .4058, laborBaseline: { monthlyPerOperatorUsd: 0, costPerTicketUsd: 0, configured: false }, monthlySavingsPerOperatorUsd: null, byVendor: [{ vendor: 'openai', cost_usd: 90.44, rows: 420, sources: ['metered'] }, { vendor: 'gcp', cost_usd: 70.04, rows: 1, sources: ['invoice'] }, { vendor: 'anthropic', cost_usd: 20.08, rows: 42, sources: ['metered'] }, { vendor: 'cloudflare', cost_usd: 1.25, rows: 1, sources: ['invoice'] }], byService: [{ service: 'gcp-meeting-intel', vendor: 'gcp', cost_usd: 13.77, rows: 1, sources: ['invoice'], cost_kinds: ['fixed'] }], byModel: [{ model_name: 'gpt-5.4', interactions: 365, tokens: 2_900_000, cost_usd: 78.04 }, { model_name: 'gemini-2.5-flash', interactions: 55, tokens: 1_920_000, cost_usd: 14.10 }] }
}

export { isoDaysFromNow }

// ── Per-charge transfers (GET /api/accounting/transfer-reconciliation) ─────
//
// One flagged charge (a refund that never clawed back the client's transfer)
// against an otherwise clean sweep, so the demo shows what a finding looks like.
export function demoTransferReconciliation(): TransferReconResponse {
  const at = new Date(Date.now() - 5 * 3600_000).toISOString()
  return {
    lastRun: {
      at, ok: true, scanned: 512, connect: 498, checked: 498, mismatch: 1, unverified: 0,
      unledgered: 0, truncated: false, probeBites: true, payoutsItemized: 3,
    },
    counts: { ok: 497, mismatch: 1, unverified: 0 },
    payouts: { itemized: 18, pending: 1, unavailable: 0 },
    recentPayouts: [
      { id: 'po_demo_1', stripeAccountId: 'acct_demo', clientId: DEMO_CLIENT.id, amountCents: 184250, status: 'paid', arrivalDate: at.slice(0, 10), itemsStatus: 'done' },
      { id: 'po_demo_2', stripeAccountId: 'acct_demo', clientId: DEMO_CLIENT.id, amountCents: 162910, status: 'paid', arrivalDate: null, itemsStatus: 'pending' },
    ],
    mismatches: [{
      stripeChargeId: 'ch_demo_3QvR8', stripeIntentId: 'pi_demo_3QvR8', sessionTxnId: 'txn_demo_812', clientId: DEMO_CLIENT.id,
      entityId: null, zoneId: 'GA1350', chargeAmountCents: 1800, chargeCreatedAt: at, transferId: 'tr_demo_3QvR8',
      transferDestination: 'acct_demo', expectedDestination: 'acct_demo', transferCents: 1800, applicationFeeCents: 279,
      expectedNetCents: 1521, actualNetCents: 1521, reversedRefundCents: 0, reversedDisputeCents: 0, reversedOtherCents: 0,
      stripeRefundedCents: 1800, ledgerDisputedCents: 0, clientKeptCents: 1521, status: 'mismatch',
      issues: ['refund_not_reversed'], firstFlaggedAt: at, checkedAt: at,
    }],
  }
}

export function demoConnectPayoutTrace(id: string): ConnectPayoutTraceResponse {
  const p = demoTransferReconciliation().recentPayouts.find(x => x.id === id) ?? demoTransferReconciliation().recentPayouts[0]!
  return {
    payout: p,
    items: p.itemsStatus !== 'done' ? [] : [
      { balanceTxnId: 'txn_demo_a', type: 'payment', amountCents: 2400, netCents: 2028, transferId: 'tr_demo_a', stripeChargeId: 'ch_demo_a', sessionTxnId: 'txn_demo_801', zoneId: 'GA1350', reconStatus: 'ok' },
      { balanceTxnId: 'txn_demo_b', type: 'payment', amountCents: 214000, netCents: 182222, transferId: 'tr_demo_b', stripeChargeId: 'ch_demo_b', sessionTxnId: 'txn_demo_802', zoneId: 'GA1350', reconStatus: 'ok' },
    ],
    itemsNetCents: p.itemsStatus !== 'done' ? 0 : 184250,
    unlinkedCount: 0,
  }
}

/** The demo's CSV for a statement: the same columns the Worker's CSV carries. */
export function demoPayoutStatementCsv(s: PayoutStatement): string {
  const d = (c: number) => (c / 100).toFixed(2)
  const rows: string[] = [
    `Payout,${s.payout.id}`,
    `Deposit,${d(s.payout.amountCents)},${s.payout.currency.toUpperCase()}`,
    `Arrival date,${s.payout.arrivalDate ?? ''}`,
    `Statement,${s.complete ? 'Complete: the lines add up to the deposit' : 'INCOMPLETE'}`,
    '',
    'Line,Charged at,Lot,Session,Gross,Parkify fee,Card processing,Tax held,Parkify fee + card processing,Net to you,Stripe balance transaction',
    ...s.lines.map(l => [l.kind, l.chargedAt ?? '', `"${l.zoneName ?? ''}"`, l.sessionTxnId ?? '', d(l.grossCents),
      d(l.parkifyFeeCents), d(l.cardProcessingCents), d(l.taxHeldCents), d(l.combinedFeesCents), d(l.netCents), l.balanceTxnId].join(',')),
    '',
    `Total of lines,${d(s.totals.linesNetCents)}`,
    `Deposit,${d(s.totals.payoutCents)}`,
  ]
  return rows.join('\r\n') + '\r\n'
}
